/* Auto-scrolling "recently booked" ticker.
   Curated fake bookings for now (test data in DB would look bad).
   Each entry uses an `hoursAgo` offset so the timestamps shift every visit
   and always read fresh ("2 hrs ago", "Yesterday", "3 days ago" etc.).
   Swap back to a live feed once real bookings are flowing. */
import React, { useMemo } from "react";
import { Sparkles, Clock, Users } from "lucide-react";

const FAKE_BOOKINGS = [
 { name: "Maddy", event_type: "Hens cruise", guests: "38", hoursAgo: 2 },
 { name: "Liam", event_type: "30th birthday", guests: "55", hoursAgo: 5 },
 { name: "Renee", event_type: "EOFY corporate", guests: "90", hoursAgo: 9 },
 { name: "Olivia", event_type: "Wedding charter", guests: "60", hoursAgo: 14 },
 { name: "Patrick", event_type: "Client entertainment", guests: "20", hoursAgo: 22 },
 { name: "Aaron", event_type: "40th birthday", guests: "45", hoursAgo: 30 },
 { name: "Hannah", event_type: "Engagement party", guests: "42", hoursAgo: 40 },
 { name: "Sam", event_type: "Engagement cruise", guests: "28", hoursAgo: 52 },
 { name: "Sophie", event_type: "Christmas party", guests: "70", hoursAgo: 70 },
 { name: "Jack", event_type: "Product launch", guests: "85", hoursAgo: 88 },
 { name: "Chloe", event_type: "21st birthday", guests: "48", hoursAgo: 110 },
 { name: "Noah", event_type: "Networking cruise", guests: "65", hoursAgo: 130 },
 { name: "Isla", event_type: "Bridal shower", guests: "30", hoursAgo: 152 },
 { name: "Ethan", event_type: "NYE 2026", guests: "12", hoursAgo: 170 },
 { name: "Ruby", event_type: "50th birthday", guests: "52", hoursAgo: 200 },
 { name: "Cooper", event_type: "Private charter", guests: "100", hoursAgo: 230 }];


function relativeLabel(hoursAgo) {
 if (hoursAgo < 1) return "Just now";
 if (hoursAgo < 24) return `${hoursAgo} hr${hoursAgo === 1 ? "" : "s"} ago`;
 const days = Math.round(hoursAgo / 24);
 if (days === 1) return "Yesterday";
 if (days < 7) return `${days} days ago`;
 const weeks = Math.round(days / 7);
 return `${weeks} wk${weeks === 1 ? "" : "s"} ago`;
}

export default function BookingTicker() {
 const items = useMemo(() => {
  return FAKE_BOOKINGS.map((b) => ({
   name: b.name,
   event_type: b.event_type,
   guests: b.guests,
   when: relativeLabel(b.hoursAgo),
  }));
 }, []);

 // duplicate items for seamless marquee loop
 const loop = [...items, ...items];

 return (
  <div className="relative bg-[#06091A] border-y border-amber-400/20 overflow-hidden" data-testid="booking-ticker">
   <div className="relative flex items-center gap-3 py-3 px-4 sm:px-6">
    <div className="hidden sm:flex items-center gap-2 shrink-0 pr-4 border-r border-white/10">
     <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
     </span>
     <span className="text-amber-200 text-[10px] font-black tracking-[0.32em] uppercase">Recently booked</span>
    </div>

    <div className="relative flex-1 overflow-hidden">
     <div className="flex items-center gap-8 sm:gap-12 ticker-track" style={{ width: "max-content" }}>
      {loop.map((it, i) => (
       <div key={`${i}-${it.name}-${it.event_type}`} className="flex items-center gap-3 text-white/85 whitespace-nowrap" data-testid="ticker-item">
        <Sparkles className="w-3.5 h-3.5 text-amber-300 shrink-0" strokeWidth={2} />
        <span className="text-amber-100 font-black text-[13px] tracking-wide">{it.name}</span>
        <span className="text-white/45 text-[12px]">·</span>
        <span className="text-white/85 text-[12.5px] font-semibold">{it.event_type}</span>
        {it.guests && (
         <>
          <span className="text-white/30 text-[12px]">·</span>
          <span className="inline-flex items-center gap-1 text-white/65 text-[11.5px]">
           <Users className="w-3 h-3" /> {it.guests}
          </span>
         </>
        )}
        <span className="text-white/30 text-[12px]">·</span>
        <span className="inline-flex items-center gap-1 text-white/55 text-[11px] uppercase tracking-wider">
         <Clock className="w-3 h-3" /> {it.when}
        </span>
       </div>
      ))}
     </div>
    </div>
   </div>

   <style>{`
    .ticker-track {
     animation: tickerScroll 55s linear infinite;
    }
    .ticker-track:hover { animation-play-state: paused; }
    @keyframes tickerScroll {
     from { transform: translateX(0); }
     to { transform: translateX(-50%); }
    }
   `}</style>
  </div>);

}

